import React, { useState } from "react";
import { Switch, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import ShareLink from "./ShareLink";
import { getRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";

import settingIcon from "../../assets/images/setting-icon-red.svg";
import editIcon from "../../assets/images/edit-icon.svg";
import previewIcon from "../../assets/images/preview-icon.svg";
import helpIcon from "../../assets/images/helpIcon.svg";
import upArrow from "../../assets/images/right-arrow.svg";
import downArrow from "../../assets/images/new-down-arrow.svg";
import analyticsIcon from "../../assets/images/analytics.svg";

import "./MicrositeHeader.scss";

const toDisplayValue = (value) =>
  value === null || value === undefined || value === "" ? "" : String(value);

const normalizeMicrositeStatus = (status) =>
  String(status || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

export const getMicrositeApprovalStatus = (status) => {
  const normalized = normalizeMicrositeStatus(status);
  if (!normalized) {
    return null;
  }
  if (normalized === "APPROVED") {
    return { label: "Approved", variant: "approved" };
  }
  if (
    normalized === "WAITING_FOR_APPROVAL" ||
    normalized === "WAITING" ||
    normalized === "PENDING"
  ) {
    return { label: "Waiting for Approval", variant: "waiting" };
  }
  if (normalized === "DENIED" || normalized === "REJECTED") {
    return { label: "Rejected", variant: "rejected" };
  }
  return null;
};

export const isMicrositeApproved = (status) =>
  getMicrositeApprovalStatus(status)?.variant === "approved";

// Accordion + Site Features stay locked until status is Approved.
export const isMicrositeAccordionLocked = (status) => !isMicrositeApproved(status);

const MicrositeHeader = ({
  data,
  activeKey,
  micrositeData,
  micrositeId,
  switchState,
  partner,
  partnerID,
  navigate,

  redirectMicroSite,
  handleMenus,
  handleHelpClick,
  setShowSubscriptionModal,
  setPublishConfirmation,
  setUnPublishConfirmation,
  getMicrositeUrl,
}) => {
  const [partnerContact, setPartnerContact] = useState({
    email: "",
    mobileNumber: "",
  });
  const [contactLoaded, setContactLoaded] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  const approvalStatus = getMicrositeApprovalStatus(data?.approvalStatus);
  const siteApproved = isMicrositeApproved(data?.approvalStatus);
  const isAccordionLocked = isMicrositeAccordionLocked(data?.approvalStatus);
  const isAccordionOpen =
    !isAccordionLocked && String(data.id) === String(activeKey);
  const lockedTooltip = "Site must be approved before it can be opened";
  console.log('test')

  const loadPartnerContact = async () => {
    if (contactLoaded || contactLoading) return;
    if (!partnerID) {
      setPartnerContact({ email: "", mobileNumber: "" });
      setContactLoaded(true);
      return;
    }

    setContactLoading(true);
    try {
      const response = await getRequest(
        userServiceApi.partnerDetails(partnerID)
      );
      const details = response?.data || {};
      setPartnerContact({
        email: toDisplayValue(details.email),
        mobileNumber: toDisplayValue(details.mobileNumber),
      });
    } catch (error) {
      setPartnerContact({ email: "", mobileNumber: "" });
    } finally {
      setContactLoaded(true);
      setContactLoading(false);
    }
  };

  const handleRejectionTooltipVisibleChange = (visible) => {
    if (visible) {
      loadPartnerContact();
    }
  };

  const rejectionTooltipContent = (
    <div className="ms-rejection-reason-tooltip-content">
      <div className="ms-rejection-section">
        <div className="ms-rejection-section-label">Reason</div>
        <div className="ms-rejection-reason-text">
          {data?.rejectionReason || "No reason provided"}
        </div>
      </div>

      <div className="ms-rejection-divider" />

      <div className="ms-rejection-section">
        <div className="ms-rejection-contact-heading">Contact Us</div>
        <div className="ms-rejection-contact-list">
          <div className="ms-rejection-contact-row">
            <span className="ms-rejection-contact-label">Email</span>
            <span className="ms-rejection-contact-value">
              {partnerContact.email}
            </span>
          </div>
          <div className="ms-rejection-contact-row">
            <span className="ms-rejection-contact-label">Phone Number</span>
            <span className="ms-rejection-contact-value">
              {partnerContact.mobileNumber}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`ms-header${isAccordionLocked ? " ms-header--locked" : ""}`}>
      {/* LEFT SECTION */}
      <div className="ms-header-left">
        {micrositeData?.length >= 1 && (
          <div className="header-handler">
            <Tooltip title={isAccordionLocked ? lockedTooltip : null}>
              <img
                src={isAccordionOpen ? downArrow : upArrow}
                alt="toggle"
                className={
                  isAccordionLocked ? "accordion-toggle--disabled" : undefined
                }
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (isAccordionLocked) return;
                  redirectMicroSite(data.id);
                }}
              />
            </Tooltip>
          </div>
        )}

        <div className="ms-logo-container">
          <img
            className="org-admin-logo"
            src={data?.mediaLink}
            alt={data?.name}
          />
        </div>

        <div className="ms-title-wrapper">
          <div className="ms-title">{data?.name}</div>

          {/* <div className="ms-edit-link">
            <a onClick={() => handleMenus(data?.id, "edit", true)}>
              Edit Site name & logo
            </a> 
          </div> */}

          <div
            title="Edit Site name & logo"
            onClick={() => handleMenus(data?.id, "edit", true)}>
            <img
              className="ms-header-icon"
              src={editIcon}
              alt="Edit Site name & logo"
            />
          </div>
        </div>
      </div>

      <div className="ms-header-middle">
        <Tooltip title={isAccordionLocked ? lockedTooltip : null}>
          <div
            className={`app-features-icon-container${isAccordionLocked ? " app-features-icon-container--disabled" : ""
              }`}
            onClick={() => {
              if (isAccordionLocked) return;
              setShowSubscriptionModal(true);
            }}
          >
            <div>Feature</div>

            <img
              src={settingIcon}
              alt="Site Features"
              className="app-features-icon-img"
            />

            <div>Settings</div>
          </div>
        </Tooltip>
      </div>

      <div className="ms-header-actions-rail">
        {approvalStatus ? (
          <span
            className={`ms-approval-status ms-approval-status--${approvalStatus.variant}`}
          >
            <span className="ms-approval-status-label">
              {approvalStatus.label}
            </span>
            {approvalStatus.variant === "rejected" && (
              <Tooltip
                color="#ffffff"
                placement="top"
                trigger="click"
                overlayClassName="ms-rejection-reason-tooltip"
                overlayInnerStyle={{
                  maxWidth: 420,
                  padding: 0,
                }}
                title={rejectionTooltipContent}
                onVisibleChange={handleRejectionTooltipVisibleChange}
              >
                <span
                  className="ms-approval-status-info"
                  role="button"
                  tabIndex={0}
                  aria-label="Rejection reason"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                >
                  <QuestionCircleOutlined />
                </span>
              </Tooltip>
            )}
          </span>
        ) : (
          <span className="ms-approval-status ms-approval-status--placeholder" aria-hidden />
        )}

        <div className="ms-header-right">
          {micrositeId === data.id && (
            <div
              className="header-action"
              onClick={handleHelpClick}
            >
              <img src={helpIcon} alt="Help" />
              <span className="action-label">Help</span>
            </div>
          )}

          {micrositeId === data.id && (
            <div
              className="header-action"
              onClick={() =>
                window.open(
                  `${getMicrositeUrl(
                    partner
                  )}/microsite/${encodeURIComponent(
                    data?.name
                  )}?partnerId=${partnerID}&partnerName=${encodeURIComponent(
                    partner
                  )}`,
                  "_blank"
                )
              }
            >
              <img src={previewIcon} alt="Preview" />
              <span className="action-label">Preview</span>
            </div>
          )}

          <Tooltip
            placement="topLeft"
            title={
              siteApproved
                ? null
                : "Site must be approved before it can be published"
            }
          >
            <div
              className={`header-action publish-action${siteApproved ? "" : " publish-action--disabled"
                }`}
            >
              <Switch
                checked={switchState}
                disabled={!siteApproved}
                checkedChildren="Online"
                unCheckedChildren="Offline"
                onChange={(checked) => {
                  if (checked) {
                    setPublishConfirmation(true);
                  } else {
                    setUnPublishConfirmation(true);
                  }
                }}
                className={`switch-${switchState ? "published" : "unpublished"
                  }`}
              />

              <span className="action-label">Publish</span>
            </div>
          </Tooltip>

          {micrositeId === data.id && (
            <div className="header-action">
              <ShareLink
                shareUrl={`${getMicrositeUrl(
                  partner
                )}/microsite/${encodeURIComponent(
                  data?.name
                )}?partnerId=${partnerID}&partnerName=${encodeURIComponent(
                  partner
                )}&fromList=false`}
              />
            </div>
          )}

          {micrositeId === data.id &&
            (data?.isPublished ? (
              <div
                className="header-action"
                onClick={() =>
                  navigate(`/microsite-analytics/${data.id}`, {
                    state: {
                      micrositeName: data.name,
                      page: "home",
                      orgId: "",
                    },
                  })
                }
              >
                <img src={analyticsIcon} alt="Analytics" />
                <span className="action-label">Engagement</span>
              </div>
            ) : (
              <Tooltip title="Site should be published at least once to view engagement data">
                <div
                  className="header-action disabled"
                >
                  <img src={analyticsIcon} alt="Analytics" />
                  <span className="action-label">Engagement</span>
                </div>
              </Tooltip>
            ))}
        </div>
      </div>
    </div>
  );
};

export default MicrositeHeader;